import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Row, Col, Input, Select, Table, Avatar, Typography, Space, Spin, FloatButton, Grid } from 'antd';
import { SearchOutlined, VerticalAlignTopOutlined, FilterOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;

const { Text } = Typography;
const { Option } = Select;



const PackagePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterVisible, setFilterVisible] = useState(false);
  const pageSize = 20;
  
  // 使用Ant Design的响应式栅格系统
  const screens = useBreakpoint();
  
  // 筛选条件
  const [filters, setFilters] = useState({
    name: '',
    device: null,
    thirdParty: null,
    available: null,
    p360: null,
    lvl: null,
  });



// 新增状态管理
const [total, setTotal] = useState(0);
const [afCount, setAfCount] = useState(0);
const [adjustCount, setAdjustCount] = useState(0);

// 新增统计接口请求
const loadSummaryData = useCallback(async () => {
  try {
    const response = await fetch(`https://novaemu.com/novammp/nova_service/config/packages_summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    const result = await response.json();
    if (result.success) {
      setTotal(result.data.total);
      setAfCount(result.data.af_count);
      setAdjustCount(result.data.adjust_count);
    }
  } catch (error) {
    console.error('统计接口请求失败:', error);
  }
}, []);

 
  // 新增useEffect
  useEffect(() => {
    loadSummaryData();
  }, [loadSummaryData]);

  
  // const allData = useRef(null);
  const loadDataRef = useRef(null);
  // 表格容器引用
  const tableContainerRef = useRef(null);
  

  // 修改loadData函数
  const loadData = useCallback(async (page = currentPage) => {
    if (loading) return;
  
    setLoading(true);

    try {
      const response = await fetch(`https://novaemu.com/novammp/nova_service/config/packages_details?pkgs=${filters.name || ''}&plat=${filters.device || ''}&mmp=${filters.thirdParty || ''}&status=1&p360=${
        filters.p360 === 'yes' ? 1 : filters.p360 === 'no' ? 0 : ''
      }&lvl=${filters.lvl === 'yes' ? 1 : filters.lvl === 'no' ? 0 : filters.lvl === 'both' ? 2 : ''}&page=${page}&count=${pageSize}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      
      });
  
      const result = await response.json();
      
      if (result.success) {
        setData(prev => page === 1 ? result.data : [...prev, ...result.data]);
        setCurrentPage(page); // 新增状态更新
        setHasMore(result.data.length >= pageSize);
      }
    } catch (error) {
      console.error('接口请求失败:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, currentPage, filters, pageSize]);
  
  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setCurrentPage(1); // 重置到第一页
  };
  
  // 初始化和筛选条件变化时重新加载数据
  useEffect(() => {
    setData([]);
    setCurrentPage(1);
    setHasMore(true);
    // 使用loadDataRef.current调用loadData，避免依赖循环
    if (loadDataRef.current) {
      loadDataRef.current(1);
    }
  }, [filters]); // 不需要依赖loadData
  
  // 处理滚动加载
  const handleScroll = useCallback(() => {
    // 检查是否滚动到底部附近
    if (
      window.innerHeight + window.pageYOffset + 200 >= document.body.offsetHeight &&
      !loading && 
      hasMore
    ) {
      if (loadDataRef.current) {
        loadDataRef.current(currentPage + 1);
      }
    }
  }, [loading, hasMore, currentPage]); // 移除loadData依赖
  
  // 添加全局滚动事件监听
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  // 确保表格完全展开，不限制高度
  useEffect(() => {
    if (tableContainerRef.current) {
      const container = tableContainerRef.current;
      container.style.maxHeight = 'none';
      container.style.height = 'auto';
    }
  }, [data.length]);
  
  // 根据屏幕大小调整表格列
  const getColumns = () => {
    // 移动端显示较少的列
    if (!screens.md) {
      return [
        {
          title: 'Package Name',
          dataIndex: 'pkg_name',
          fixed: 'left',
          width: 140,
          render: (_, record) => (
            <Space>
              <Avatar src={record.icon_url} size="small" shape="square" />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text strong>{record.pkg_name}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>{record.bundle_id}</Text>
              </div>
            </Space>
          ),
        },
        {
          title: 'Device',
          dataIndex: 'plat',
           width: 100,
        },
        {
          title: 'Third Party',
          dataIndex: 'mmp',
          width: 100,
          render: (text) => <Text>{text}</Text>,
        },
        {
          title: 'P360',
          dataIndex: 'p360',
          key: 'p360',
          width: 50,
          render: (p360) => (
            <Text style={{ color: p360 ? '#52c41a' : '#f5222d' }}>{p360 ? 'Yes' : 'No'}</Text>
          ),
        },
        {
          title: 'Lvl',
          dataIndex: 'lvl',
          key: 'lvl',
          width: 50,
          render: (lvl) => (
            <Text style={{ color: lvl ? '#52c41a' : '#f5222d' }}>{lvl === 2 ? 'Both' : lvl === 1 ? 'Yes' : 'No'}</Text>
          ),
        },
      ];
    }
    
    // 桌面端显示完整列
    return [
      {
        title: 'ID',
        dataIndex: 'index',
        width: 80,
        render: (_, record, index) => index + 1,
      },
      {
        title: 'Package Name',
        dataIndex: 'pkg_name',
        render: (_, record) => (
          <Space>
            <Avatar src={record.icon_url} size={40} shape="square" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text strong>{record.pkg_name}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>{record.bundle_id}</Text>
            </div>
          </Space>
        ),
      },
      {
        title: 'Device',
        dataIndex: 'plat',
      },
      {
        title: 'Third Party',
        dataIndex: 'mmp',
      },
      {
        title: 'P360',
        dataIndex: 'p360',
        key: 'p360',
        render: (p360) => (
          <Text style={{ color: p360 ? '#52c41a' : '#f5222d' }}>{p360 ? 'Yes' : 'No'}</Text>
        ),
      },
      {
        title: 'Lvl',
        dataIndex: 'lvl',
        key: 'lvl',
        render: (lvl) => (
          <Text style={{ color: lvl ? '#52c41a' : '#f5222d' }}>{lvl === 2 ? 'Both' : lvl === 1 ? 'Yes' : 'No'}</Text>
        ),
      },
    ];
  };
  
  return (
    <div>
      {/* 统计信息 */}
      <Row gutter={screens.md ? 8 : 4} style={{ marginBottom: screens.md ? 24 : 16 , textAlign: 'center' }} >
        <Col xs={24} sm={24} md={8}>
          <div style={{ borderRight: screens.md ?'1px solid #e5e6ec': 'none', marginBottom: '16px' }}>
            <div style={{ fontWeight: 'bold',fontSize: '3rem', color:'#220d4e' ,lineHeight: '3.5rem' }}>{total}</div>
            <div style={{ color:'#220d4e' }}>All Packages</div>
          </div>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <div style={{ borderRight: screens.md ?'1px solid #e5e6ec': 'none' ,marginBottom: '16px'}}>
            <div style={{ fontWeight: 'bold',fontSize: '3rem', color:'#220d4e' ,lineHeight: '3.5rem' }}>{afCount}</div>
            <div style={{ color:'#220d4e' }}>appsflyer Packages</div>
          </div>
        </Col>
        <Col xs={24} sm={24} md={8} >
          <div >
            <div style={{ fontWeight: 'bold',fontSize: '3rem' , color:'#220d4e' ,lineHeight: '3.5rem' }}>{adjustCount}</div>
            <div style={{ color:'#220d4e' }}>adjust Packages</div>
          </div>
        </Col>
      </Row>
      
      {/* 移动端筛选按钮 */}
      {!screens.md && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Input
            placeholder="Package Name"
            prefix={<SearchOutlined />}
            value={filters.name}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            allowClear
            style={{ width: 'calc(100% - 50px)' }}
          />
          <div 
            onClick={() => setFilterVisible(!filterVisible)}
            style={{ 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#f0f0f0',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            <FilterOutlined style={{ fontSize: 18 }} />
          </div>
        </div>
      )}
      
      {/* 筛选条件 */}
      <Card 
        style={{ 
          marginBottom: 24, 
          borderRadius: 12, 
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
          display: (!screens.md && !filterVisible) ? 'none' : 'block'
        }}
      >
        <Row gutter={screens.md ? 16 : 8}>
          {screens.md && (
            <Col xs={24} sm={8} md={6} lg={4}>
              <Input
                placeholder="Package Name"
                prefix={<SearchOutlined />}
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                allowClear
                style={{ marginBottom:  8 }}
              />
            </Col>
          )}
          <Col xs={24} sm={8} md={6} lg={4}>
            <Select
              style={{ width: '100%', marginBottom:  8 }}
              placeholder="Device"
              value={filters.device}
              onChange={(value) => handleFilterChange('device', value)}
              allowClear
            >
              <Option value="android">Android</Option>
              <Option value="ios">iOS</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6} lg={4}>
            <Select
              style={{ width: '100%', marginBottom:  8 }}
              placeholder="Third Party"
              value={filters.thirdParty}
              allowClear
              onChange={(value) => handleFilterChange('thirdParty', value)}
            >
              <Option value="af">appsflyer</Option>
              <Option value="adjust">adjust</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6} lg={4}>
            <Select
              style={{ width: '100%', marginBottom:  8 }}
              placeholder="P360"
              value={filters.p360}
              allowClear
              onChange={(value) => handleFilterChange('p360', value)}
            >
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
            </Select>
          </Col>
             <Col xs={24} sm={8} md={6} lg={4}>
            <Select
              style={{ width: '100%', marginBottom:  8 }}
              placeholder="lvl"
              value={filters.lvl}
              allowClear
              onChange={(value) => handleFilterChange('lvl', value)}
            >
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
              <Option value="both">Both</Option>
            </Select>
          </Col>
        </Row>
      </Card>
      
      {/* 数据表格 */}
      <div ref={tableContainerRef} style={{ overflow: 'visible' }}>
        <Table
          columns={getColumns()}
          dataSource={data}
          scroll={{ x: screens.md ? undefined : 500 }}
          pagination={false}
          loading={loading}
          style={{ width: '100%' }}
          rowKey={'id'}
        />
      </div>
      
      {/* 加载指示器 */}
      {loading && (
        <div style={{ textAlign: 'center', margin: screens.md ? '20px 0' : '15px 0', padding: screens.md ? '10px 0' : '8px 0' }}>
          <Spin  size={screens.md ? 'large' : 'default'} style={{ color: '#4361ee' }} />
        </div>
      )}
      
      {/* 滚动到底部的占位符，帮助触发滚动事件 */}
      {hasMore && <div style={{ height: screens.md ? '100px' : '80px' }} />}
      
      {/* 回到顶部按钮 */}
      <FloatButton.BackTop>
        <div style={{
          height: screens.md ? 40 : 36,
          width: screens.md ? 40 : 36,
          lineHeight: screens.md ? '40px' : '36px',
          borderRadius: 8,
          backgroundColor: '#4361ee',
          color: '#fff',
          textAlign: 'center',
          fontSize: screens.md ? 16 : 14,
          boxShadow: '0 4px 12px rgba(67, 97, 238, 0.3)',
          transition: 'all 0.3s ease',
        }}>
          <VerticalAlignTopOutlined />
        </div>
      </FloatButton.BackTop>
    </div>
  );
};

export default PackagePage;

